import { ref, computed } from 'vue';
import { BiometryType, NativeBiometric, BiometricAuthError, type AvailableResult } from 'capacitor-native-biometric';
import { captureException } from '@sentry/capacitor';

export function useBiometric() {
  const platformStore = usePlatformStore();
  const { openToast } = useUserMessages();
  const { t } = useEkI18n();

  const isAvailable = ref<AvailableResult>();
  const isBiometricButtonDisabled = ref(false);

  const icon = computed(() => platformStore.isIos ? 'fa-face-viewfinder' : 'fa-fingerprint');

  const biometricDescription = computed(() => {
    const descriptions: Record<BiometryType, string> = {
      [BiometryType.NONE]: t('login.biometric.generic'),
      [BiometryType.MULTIPLE]: t('login.biometric.generic'),
      [BiometryType.TOUCH_ID]: t('login.biometric.fingerprint'),
      [BiometryType.FINGERPRINT]: t('login.biometric.fingerprint'),
      [BiometryType.FACE_ID]: t('login.biometric.face'),
      [BiometryType.FACE_AUTHENTICATION]: t('login.biometric.face'),
      [BiometryType.IRIS_AUTHENTICATION]: t('login.biometric.face')
    };

    return isAvailable.value?.biometryType
      ? descriptions[isAvailable.value.biometryType]
      : t('login.biometric.generic');
  });

  async function checkAvailability() {
    if (platformStore.isMobile) {
      isAvailable.value = await NativeBiometric.isAvailable();
    }
  }

  async function verifyBiometricIdentity() {
    const biometricOptions = {
      reason: t('login.biometric.reason'),
      title: t('login.biometric.title'),
      subtitle: t('login.biometric.subtitle'),
      description: biometricDescription.value,
      maxAttempts: 5,
      negativeButtonText: t('buttons.cancel'),
      useFallback: false
    };

    return NativeBiometric.verifyIdentity(biometricOptions)
      .then(() => true)
      .catch((error) => {
        captureException(error);

        if (error.code === BiometricAuthError.USER_TEMPORARY_LOCKOUT.toString()) {
          openToast(t('login.toast.loginAttemptsPinCodeUnlock'), ToastType.ERROR, t('login.toast.loginError'));
        } else if (error.code === BiometricAuthError.AUTHENTICATION_FAILED.toString()) {
          openToast(t('login.toast.tooManyAttempts'), ToastType.ERROR, t('login.toast.loginError'));
        }

        return false;
      });
  }

  return {
    isAvailable,
    icon,
    biometricDescription,
    isBiometricButtonDisabled,
    checkAvailability,
    verifyBiometricIdentity
  };
}
